/**
 * E018-S008-T004: Rollup Cell View
 *
 * Simplified implementation that delegates CrossDoc logic to the
 * centralized related-cell-values utility.
 */
import { Button, uniReactRoot } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  type Cell,
  type CellRenderProps,
  createIcon,
  type DatabaseBlockDataSource,
  type DataViewCellLifeCycle,
} from '@blocksuite/affine/blocks/database';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { popRollupConfigMenu } from '../../../streamlain/database/column-menu-hooks';
import {
  getRelatedCellValues,
  loadRelatedCellValuesAsync,
  relatedValuesCacheVersion,
} from '../relation/related-cell-values';
import { AGGREGATION_META, computeRollupValue } from './aggregation';
import type {
  RollupCellJsonValue,
  RollupCellRawValue,
  RollupPropertyData,
} from './define';
import { rollupPropertyModelConfig } from './define';
import * as styles from './style.css';

/**
 * Manager for Rollup Cell logic
 */
class RollupManager {
  readonly cell: Cell<
    RollupCellRawValue,
    RollupCellJsonValue,
    RollupPropertyData
  >;
  readonly selectCurrentCell: (editing: boolean) => void;
  readonly isEditing: ReadonlySignal<boolean>;

  get propertyData() {
    return this.cell.property.data$.value;
  }

  get store() {
    return this.dataSource.doc;
  }

  get dataSource() {
    return this.cell.view.manager.dataSource as DatabaseBlockDataSource;
  }

  /**
   * Computed: The aggregated rollup value
   *
   * E018-S008-T004: Uses centralized getRelatedCellValues utility
   * instead of implementing CrossDoc logic directly.
   */
  computedResult = computed(() => {
    // Subscribe to cache updates
    void relatedValuesCacheVersion.value;

    const propertyData = this.propertyData;
    const { sourceRelationFieldId, targetFieldId, aggregation, separator } =
      propertyData;

    // Not configured
    if (!sourceRelationFieldId || !targetFieldId) {
      return { value: null, state: 'available', loadInfo: null } as const;
    }

    // Get related row IDs from the source relation field
    const relationValue = this.dataSource.cellValueGet(
      this.cell.rowId,
      sourceRelationFieldId
    ) as string[] | null;

    // Get relation property data (contains targetDatabaseId, targetDocId)
    const relationData = this.dataSource.propertyDataGet(
      sourceRelationFieldId
    ) as
      | {
          targetDatabaseId?: string;
          targetDocId?: string;
        }
      | undefined;

    if (!relationData?.targetDatabaseId) {
      return { value: null, state: 'available', loadInfo: null } as const;
    }

    // E018-S008-T004: Use centralized utility for InDoc/CrossDoc handling
    const result = getRelatedCellValues(
      this.store,
      this.store.id,
      relationData,
      relationValue ?? [],
      targetFieldId
    );

    return {
      state: result.state,
      loadInfo: result.loadInfo,
      value:
        result.state === 'available' && !result.needsAsyncLoad
          ? computeRollupValue(result.values, aggregation, separator)
          : null,
    };
  });

  constructor(
    props: CellRenderProps<
      RollupPropertyData,
      RollupCellRawValue,
      RollupCellJsonValue
    >
  ) {
    this.cell = props.cell;
    this.selectCurrentCell = props.selectCurrentCell;
    this.isEditing = props.isEditing$;
  }
}

/**
 * Rollup Cell Component (Read-Only)
 */
const RollupCellComponent: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<RollupPropertyData, RollupCellRawValue, RollupCellJsonValue>
> = (props, ref): ReactNode => {
  const t = useI18n();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const manager = useMemo(() => new RollupManager(props), []);

  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => false, // Read-only, no editing
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );

  const result = useSignalValue(manager.computedResult);
  const computedValue = result.value;
  const propertyData = useSignalValue(manager.cell.property.data$);
  const readonly = useSignalValue(manager.cell.property.readonly$);
  const loadInfo = result.loadInfo;

  // E018-S008-T004: Load CrossDoc values asynchronously using centralized utility
  useEffect(() => {
    if (!loadInfo) return;

    loadRelatedCellValuesAsync(loadInfo).catch(console.error);
  }, [loadInfo]);

  // Unconfigured State
  if (!propertyData.sourceRelationFieldId || !propertyData.targetFieldId) {
    const openRollupSettings = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      popRollupConfigMenu(
        manager.cell.property,
        manager.cell.view,
        event.currentTarget
      );
    };
    return (
      <div className={styles.cellContainer}>
        {readonly ? null : (
          <Button
            variant="secondary"
            className={styles.configureButton}
            onClick={openRollupSettings}
          >
            {t['com.streamlain.streamlain.common.configure']()}
          </Button>
        )}
      </div>
    );
  }

  if (result.state !== 'available') {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.emptyState}>
          {result.state === 'restricted'
            ? t['com.streamlain.streamlain.rollup.access-denied']()
            : t['com.streamlain.streamlain.rollup.source-unavailable']()}
        </span>
      </div>
    );
  }

  // Empty State (no related rows)
  if (computedValue === null || computedValue === undefined) {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.emptyState}>-</span>
      </div>
    );
  }

  // Array values (show_original, show_unique)
  if (Array.isArray(computedValue)) {
    const maxVisible = 3;
    const visibleItems = computedValue.slice(0, maxVisible);
    const remainingCount = computedValue.length - maxVisible;

    return (
      <div className={styles.cellContainer}>
        <div className={styles.arrayContainer}>
          {visibleItems.map((item, index) => (
            <span key={index} className={styles.arrayItem}>
              {item}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className={styles.moreCount}>+{remainingCount}</span>
          )}
        </div>
      </div>
    );
  }

  // Percent values
  if (
    propertyData.aggregation === 'percent_empty' ||
    propertyData.aggregation === 'percent_filled'
  ) {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.percentValue}>{computedValue}</span>
      </div>
    );
  }

  // Number values
  if (typeof computedValue === 'number') {
    const formatted = Number.isInteger(computedValue)
      ? computedValue.toString()
      : computedValue.toFixed(2);

    const meta = AGGREGATION_META[propertyData.aggregation];

    return (
      <div className={styles.cellContainer}>
        <span className={styles.numberValue}>
          {meta?.icon && (
            <span style={{ marginRight: '4px' }}>{meta.icon}</span>
          )}
          {formatted}
        </span>
      </div>
    );
  }

  // String values (concatenate)
  return (
    <div className={styles.cellContainer}>
      <span>{String(computedValue)}</span>
    </div>
  );
};

const RollupCell = forwardRef(RollupCellComponent);

/**
 * Property Config - registers the Rollup type
 */
export const rollupPropertyConfig =
  rollupPropertyModelConfig.createPropertyMeta({
    icon: createIcon('ViewLayersIcon'),
    cellRenderer: {
      view: uniReactRoot.createUniComponent(RollupCell),
    },
  });
